import CrudPage, { iconTitleColumn, textColumn, orderColumn, badgeColumn } from "@/admin/CrudPage";
import { Star } from "lucide-react";

const common = { imageHelp: "Paste a full image URL (e.g. from Unsplash or your CDN)." };

export const ServicesAdmin = () => (
  <CrudPage config={{
    resource: "services", testPrefix: "services", title: "Services", description: "The eight practice areas shown across the website.", titleField: "title", publicKeys: ["services", "service"],
    columns: [iconTitleColumn("title"), textColumn("tagline", "Tagline"), orderColumn()],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", placeholder: "auto-generated from title", help: "URL path, e.g. cloud-transformation. Leave blank to auto-generate." },
      { key: "tagline", label: "Tagline", type: "text", cols: 2, required: true },
      { key: "description", label: "Description", type: "textarea", rows: 4, cols: 2, required: true },
      { key: "icon", label: "Icon", type: "icon" },
      { key: "image", label: "Image URL", type: "text", help: common.imageHelp },
      { key: "capabilities", label: "Capabilities", type: "list", help: "One per line." },
      { key: "outcomes", label: "Outcomes", type: "list", help: "One per line." },
      { key: "engagement_model", label: "Engagement model", type: "textarea", rows: 2, cols: 2 },
      { key: "related_products", label: "Related product slugs", type: "list", help: "One slug per line, e.g. ai-workforce-scheduler" },
      { key: "order", label: "Display order", type: "number" },
      { key: "featured", label: "Featured on homepage", type: "switch", default: false },
      { key: "active", label: "Visible on website", type: "switch", default: true },
    ],
  }} />
);

export const ProductsAdmin = () => (
  <CrudPage config={{
    resource: "products", testPrefix: "products", title: "Products", description: "JNT platforms showcased on the Products page and homepage.", titleField: "name", publicKeys: ["products", "product"],
    columns: [iconTitleColumn("name"), textColumn("tagline", "Tagline"), orderColumn()],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", placeholder: "auto-generated from name" },
      { key: "tagline", label: "Tagline", type: "text", cols: 2, required: true },
      { key: "description", label: "Description", type: "textarea", rows: 4, cols: 2, required: true },
      { key: "icon", label: "Icon", type: "icon" },
      { key: "image", label: "Image URL", type: "text", help: common.imageHelp },
      { key: "problem", label: "Problem it solves", type: "textarea", rows: 3, cols: 2 },
      { key: "how_it_works", label: "How it works (steps)", type: "list" },
      { key: "features", label: "Key features", type: "list" },
      { key: "integrations", label: "Integrations", type: "list" },
      { key: "industries", label: "Industries", type: "list" },
      { key: "security", label: "Security notes", type: "textarea", rows: 2, cols: 2 },
      { key: "order", label: "Display order", type: "number" },
      { key: "featured", label: "Flagship / featured", type: "switch", default: false },
      { key: "active", label: "Visible on website", type: "switch", default: true },
    ],
  }} />
);

export const IndustriesAdmin = () => (
  <CrudPage config={{
    resource: "industries", testPrefix: "industries", title: "Industries", description: "Industry verticals shown on the Industries page and homepage.", titleField: "name", publicKeys: ["industries"],
    columns: [iconTitleColumn("name"), textColumn("description", "Description"), orderColumn()],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", placeholder: "auto-generated" },
      { key: "description", label: "Description", type: "textarea", rows: 3, cols: 2, required: true },
      { key: "icon", label: "Icon", type: "icon" },
      { key: "image", label: "Image URL", type: "text", help: common.imageHelp },
      { key: "outcomes", label: "Key outcomes", type: "list", cols: 2 },
      { key: "order", label: "Display order", type: "number" },
      { key: "active", label: "Visible on website", type: "switch", default: true },
    ],
  }} />
);

export const TestimonialsAdmin = () => (
  <CrudPage config={{
    resource: "testimonials", testPrefix: "testimonials", title: "Testimonials", description: "Client quotes shown on the homepage.", titleField: "name", publicKeys: ["testimonials"],
    columns: [
      { key: "name", label: "Client", render: (it) => <div><p className="text-sm font-medium">{it.name}</p><p className="text-xs text-muted-foreground">{it.role}{it.company ? `, ${it.company}` : ""}</p></div> },
      textColumn("quote", "Quote"),
      { key: "rating", label: "Rating", className: "hidden sm:table-cell", render: (it) => <span className="inline-flex items-center gap-1 text-sm"><Star className="h-3.5 w-3.5 fill-primary text-primary" />{it.rating}</span> },
      orderColumn(),
    ],
    fields: [
      { key: "name", label: "Client name", type: "text", required: true },
      { key: "role", label: "Role / title", type: "text", placeholder: "e.g. Head of Operations" },
      { key: "company", label: "Company", type: "text", cols: 2 },
      { key: "quote", label: "Quote", type: "textarea", rows: 4, cols: 2, required: true },
      { key: "rating", label: "Rating (1-5)", type: "number", default: 5 },
      { key: "order", label: "Display order", type: "number" },
      { key: "active", label: "Visible on website", type: "switch", default: true },
    ],
  }} />
);

export const FaqsAdmin = () => (
  <CrudPage config={{
    resource: "faqs", testPrefix: "faqs", title: "FAQs", description: "Frequently asked questions shown on the homepage and detail pages.", titleField: "question", publicKeys: ["faqs"],
    columns: [
      { key: "question", label: "Question", render: (it) => <p className="max-w-md text-sm font-medium">{it.question}</p> },
      badgeColumn("category", "Category"),
      orderColumn(),
    ],
    fields: [
      { key: "question", label: "Question", type: "text", cols: 2, required: true },
      { key: "answer", label: "Answer", type: "textarea", rows: 4, cols: 2, required: true },
      { key: "category", label: "Category", type: "select", options: ["General", "Engagement", "Products", "Security", "Support"] },
      { key: "order", label: "Display order", type: "number" },
      { key: "active", label: "Visible on website", type: "switch", default: true },
    ],
  }} />
);
